import React, { useEffect, useMemo, useState } from "react";
import {
    apiConstants,
    get,
    getDownload,
    postMultipart,
} from "../../common/constants";
import {
    FormProvider,
    RHFFileField,
} from "../../common/hook-form";
import { useForm } from "react-hook-form";
import {
    Box,
    Button,
    CircularProgress,
    Grid,
    IconButton,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";
import { formatDate } from "../../helpers/UtilityHelper";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import ProzoDataGrid from "../../common/ProzoDataGrid";
import { useSnackbar } from "notistack";
import MUIModal from "../../components/MuiModal";
import PageTitle from "../../components/PageTitle";
import jwt_decode from "jwt-decode";
import { getToken } from "../../helpers/ReduxHelpers";
import CopyIcon from "../../common/CopyIcon";

// ─── Palette ─────────────────────────────────────────────────────────────────
const P = {
    primary: "#1565c0",
    primaryLight: "#e3f2fd",
    primaryBorder: "#90caf9",
    success: "#2e7d32",
    successLight: "#e8f5e9",
    warning: "#e65100",
    warningLight: "#fff3e0",
    warningBorder: "#ffb74d",
    error: "#c62828",
    errorLight: "#ffebee",
    grey50: "#fafafa",
    grey100: "#f5f5f5",
    grey200: "#eeeeee",
    grey300: "#e0e0e0",
    grey500: "#9e9e9e",
    grey600: "#757575",
    grey800: "#424242",
    grey900: "#212121",
    white: "#ffffff",
};

const RESET_FIELDS = [
    "terminalStatus",
    "pt_order_history",
    "lifeCycleComplete",
    "orderStatus",
    "order_history",
    "orderSubStatus",
];

// ─── Visual primitives ──────────────────────────────────────────────────────
const StatusPill = ({ progress }) => {
    const map = {
        COMPLETED: { bg: P.successLight, color: P.success, border: "#a5d6a7", label: "Completed", icon: <CheckCircleOutlineIcon sx={{ fontSize: 12 }} /> },
        IN_PROGRESS: { bg: P.warningLight, color: P.warning, border: P.warningBorder, label: "In Progress", icon: <HourglassBottomIcon sx={{ fontSize: 12 }} /> },
        FAILED: { bg: P.errorLight, color: P.error, border: "#ef9a9a", label: "Failed", icon: <ErrorOutlineIcon sx={{ fontSize: 12 }} /> },
        STUCK: { bg: P.errorLight, color: P.error, border: "#ef9a9a", label: "Stuck", icon: <ErrorOutlineIcon sx={{ fontSize: 12 }} /> },
    };
    const t = map[(progress || "").toUpperCase()] || { bg: P.grey100, color: P.grey600, border: P.grey200, label: progress || "-", icon: null };
    return (
        <Box
            sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                px: 1,
                py: 0.35,
                borderRadius: 1,
                fontSize: "0.65rem",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.03em",
                backgroundColor: t.bg,
                color: t.color,
                border: `1px solid ${t.border}`,
                whiteSpace: "nowrap",
            }}
        >
            {t.icon}
            {t.label}
        </Box>
    );
};

const TaskStatusPill = ({ status }) => {
    const tones = {
        SUCCESS: { bg: P.successLight, color: P.success, border: "#a5d6a7" },
        NOT_FOUND: { bg: P.warningLight, color: P.warning, border: P.warningBorder },
        NOT_AUTHORIZED: { bg: P.warningLight, color: P.warning, border: P.warningBorder },
        ERROR: { bg: P.errorLight, color: P.error, border: "#ef9a9a" },
    };
    const t = tones[(status || "").toUpperCase()] || { bg: P.grey100, color: P.grey600, border: P.grey200 };
    return (
        <Box
            sx={{
                display: "inline-flex",
                px: 1,
                py: 0.25,
                borderRadius: 1,
                fontSize: "0.65rem",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.03em",
                backgroundColor: t.bg,
                color: t.color,
                border: `1px solid ${t.border}`,
                whiteSpace: "nowrap",
            }}
        >
            {status || "-"}
        </Box>
    );
};

// ─── Component ───────────────────────────────────────────────────────────────
const ResetTerminalStatus = () => {
    const methods = useForm();
    const { handleSubmit, reset: resetForm, watch } = methods;
    const { enqueueSnackbar } = useSnackbar();
    const { piiAccessAllow } = jwt_decode(getToken());

    const [isUploading, setIsUploading] = useState(false);
    const [jobs, setJobs] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [reload, setReload] = useState(0);
    const [lastJobUuid, setLastJobUuid] = useState(null);

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [pendingSubmitData, setPendingSubmitData] = useState(null);

    const [page, setPage] = useState(0);
    const [sizePerPage, setSizePerPage] = useState(10);
    const [modalPage, setModalPage] = useState(0);
    const [modalSizePerPage, setModalSizePerPage] = useState(10);

    const [detailsOpen, setDetailsOpen] = useState(false);
    const [detailsRows, setDetailsRows] = useState([]);
    const [detailsTitle, setDetailsTitle] = useState("AWB Details");

    const watchedFile = watch("awbFile");
    const selectedFileName = useMemo(() => {
        if (watchedFile && watchedFile[0]) return watchedFile[0].name;
        return "";
    }, [watchedFile]);

    const downloadSample = () => {
        getDownload(
            `${apiConstants.BASE_LOCAL}${apiConstants.DOWNLOAD_SAMPLE_LOCATION}?fileName=TRACK_ORDER`
        )
            .then((response) => {
                const href = URL.createObjectURL(
                    new Blob([response.data], { type: "application/octet-stream" })
                );
                const link = document.createElement("a");
                link.href = href;
                link.setAttribute("download", "reset-terminal-status-sample.csv");
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(href);
            })
            .catch((err) => {
                enqueueSnackbar("Failed to download sample", { variant: "error" });
                console.error(err);
            });
    };

    const onRequestSubmit = (data) => {
        if (!data?.awbFile || !data.awbFile[0]) {
            enqueueSnackbar("Please select a file first", { variant: "warning" });
            return;
        }
        setPendingSubmitData(data);
        setConfirmOpen(true);
    };

    const runSubmit = async () => {
        setConfirmOpen(false);
        const data = pendingSubmitData;
        if (!data) return;
        setIsUploading(true);
        try {
            const form = new FormData();
            form.append("file", data.awbFile[0]);
            form.append("remark", "Terminal status reset via admin UI");
            const res = await postMultipart(apiConstants.RESET_TERMINAL_STATUS_BULK, form);
            if (res?.data?.status === "ERROR") {
                enqueueSnackbar(res?.data?.errorMsg || "Upload failed", { variant: "error" });
            } else {
                const newUuid = res?.data?.responseObj?.uuid;
                setLastJobUuid(newUuid || null);
                enqueueSnackbar("Reset job queued successfully", { variant: "success" });
            }
            setReload((r) => r + 1);
            resetForm({ awbFile: "" });
        } catch (err) {
            const msg = err?.response?.data?.message || err?.message || "Upload failed";
            enqueueSnackbar(msg, { variant: "error" });
        } finally {
            setIsUploading(false);
            setPendingSubmitData(null);
        }
    };

    useEffect(() => {
        setIsLoading(true);
        get(`${apiConstants.RESET_TERMINAL_STATUS_STATUS}`)
            .then((res) => {
                setJobs(res?.data?.responseObj || []);
            })
            .catch(() => setJobs([]))
            .finally(() => setIsLoading(false));
    }, [reload]);

    const openDetails = (row, successOnly) => {
        const flattened = [].concat(...(row?.taskListBatch || []));
        const filtered = successOnly
            ? flattened.filter((t) => t?.status === "SUCCESS")
            : flattened.filter((t) => t?.status !== "SUCCESS");
        setDetailsRows(filtered);
        setDetailsTitle(successOnly ? "Successful AWBs" : "Failed AWBs");
        setDetailsOpen(true);
    };

    const columns = [
        {
            field: "uuid",
            headerAlign: "left",
            headerName: "Job ID",
            flex: 1.3,
            hideSortIcons: true,
            renderCell: (p) => (
                <Tooltip title={p.row?.uuid || ""}>
                    <Typography
                        sx={{
                            fontFamily: "monospace",
                            fontSize: "0.72rem",
                            color: P.grey800,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {p.row?.uuid}
                    </Typography>
                </Tooltip>
            ),
        },
        {
            field: "fileProgress",
            headerAlign: "left",
            headerName: "Status",
            flex: 0.9,
            hideSortIcons: true,
            renderCell: (p) => <StatusPill progress={p.row?.fileProgress} />,
        },
        {
            field: "createdAt",
            headerAlign: "left",
            headerName: "Submitted",
            flex: 1,
            hideSortIcons: true,
            renderCell: (p) => (
                <Typography sx={{ fontSize: "0.75rem", color: P.grey800 }}>
                    {formatDate(p.row?.createdAt)}
                </Typography>
            ),
        },
        {
            field: "createdBy",
            headerAlign: "left",
            headerName: "By",
            flex: 1.1,
            hideSortIcons: true,
            renderCell: (p) => (
                <Typography sx={{ fontSize: "0.72rem", color: P.grey800 }}>
                    {p.row?.createdBy || "-"}
                </Typography>
            ),
        },
        {
            field: "counts",
            headerAlign: "left",
            headerName: "Results",
            flex: 1.2,
            hideSortIcons: true,
            renderCell: (p) => {
                const total = p.row?.executionStatus?.totalTasks ?? 0;
                const ok = p.row?.executionStatus?.success ?? 0;
                const fail = p.row?.executionStatus?.failed ?? 0;
                return (
                    <Stack direction="row" spacing={1.25} alignItems="center">
                        <Box sx={{ fontSize: "0.75rem", color: P.grey800 }}>
                            <b>{total}</b> total
                        </Box>
                        <Box sx={{ fontSize: "0.72rem", color: P.success, fontWeight: 600 }}>✓ {ok}</Box>
                        <Box sx={{ fontSize: "0.72rem", color: P.error, fontWeight: 600 }}>✗ {fail}</Box>
                    </Stack>
                );
            },
        },
        {
            field: "actions",
            headerAlign: "left",
            headerName: "Actions",
            flex: 1.5,
            hideSortIcons: true,
            renderCell: (p) => {
                const ok = p.row?.executionStatus?.success || 0;
                const failed = p.row?.executionStatus?.failed || 0;
                const arr = p.row?.s3LinkExcelResponse;
                const resultLink = Array.isArray(arr) ? arr[0] : arr;
                const inputLink = p.row?.s3RefToFile?.signedLink;
                return (
                    <Stack direction="row" spacing={0.5} alignItems="center">
                        {resultLink && (
                            <Tooltip title="Download result CSV">
                                <IconButton
                                    size="small"
                                    component="a"
                                    href={resultLink}
                                    target="_blank"
                                    rel="noreferrer"
                                    sx={{ color: P.primary }}
                                >
                                    <FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                            </Tooltip>
                        )}
                        {piiAccessAllow && inputLink && (
                            <Tooltip title="Download input file">
                                <IconButton
                                    size="small"
                                    component="a"
                                    href={inputLink}
                                    target="_blank"
                                    rel="noreferrer"
                                    sx={{ color: P.grey600 }}
                                >
                                    <InsertDriveFileOutlinedIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                            </Tooltip>
                        )}
                        {ok > 0 && (
                            <Button
                                size="small"
                                variant="text"
                                color="success"
                                sx={{ fontSize: "0.68rem", minWidth: "auto", px: 0.75, py: 0.25, textTransform: "none" }}
                                onClick={() => openDetails(p.row, true)}
                            >
                                View ✓
                            </Button>
                        )}
                        {failed > 0 && (
                            <Button
                                size="small"
                                variant="text"
                                color="error"
                                sx={{ fontSize: "0.68rem", minWidth: "auto", px: 0.75, py: 0.25, textTransform: "none" }}
                                onClick={() => openDetails(p.row, false)}
                            >
                                View ✗
                            </Button>
                        )}
                    </Stack>
                );
            },
        },
    ];

    const detailsColumns = [
        {
            field: "awb",
            headerAlign: "left",
            headerName: "AWB",
            flex: 1,
            hideSortIcons: true,
            renderCell: (p) => (
                <Box sx={{ display: "flex", alignItems: "center", gap: "2px" }}>
                    <Typography sx={{ fontFamily: "monospace", fontSize: "0.75rem", color: P.grey900 }}>
                        {p.row?.response?.awbNumber || p.row?.data?.awbNumber || "-"}
                    </Typography>
                    <CopyIcon text={p.row?.response?.awbNumber || p.row?.data?.awbNumber} />
                </Box>
            ),
        },
        {
            field: "status",
            headerAlign: "left",
            headerName: "Status",
            flex: 0.85,
            hideSortIcons: true,
            renderCell: (p) => <TaskStatusPill status={p.row?.response?.status || p.row?.status} />,
        },
        {
            field: "error",
            headerAlign: "left",
            headerName: "Error / Remark",
            flex: 2,
            hideSortIcons: true,
            renderCell: (p) => (
                <Typography sx={{ fontSize: "0.72rem", color: P.grey800 }}>
                    {p.row?.response?.errorMessage || p.row?.errorMSG || "-"}
                </Typography>
            ),
        },
        {
            field: "prevOrderStatus",
            headerAlign: "left",
            headerName: "Prev. orderStatus",
            flex: 1,
            hideSortIcons: true,
            renderCell: (p) => (
                <Typography sx={{ fontSize: "0.72rem", color: P.grey800 }}>
                    {p.row?.response?.previousOrderStatus || "-"}
                </Typography>
            ),
        },
        {
            field: "updatedBy",
            headerAlign: "left",
            headerName: "Updated By",
            flex: 1,
            hideSortIcons: true,
            renderCell: (p) => (
                <Typography sx={{ fontSize: "0.72rem", color: P.grey800 }}>
                    {p.row?.response?.updatedBy || "-"}
                </Typography>
            ),
        },
    ];

    return (
        <Box sx={{ backgroundColor: P.grey50, minHeight: "100%", p: { xs: 1.5, md: 2.5 } }}>
            <PageTitle>Reset Order Terminal Status</PageTitle>

            {/* ── Hero upload area ── */}
            <Box
                sx={{
                    backgroundColor: P.white,
                    border: `1px solid ${P.grey200}`,
                    borderRadius: 3,
                    p: { xs: 2, md: 3.5 },
                    mb: 2.5,
                }}
            >
                <Grid container spacing={3}>
                    <Grid item xs={12} md={7}>
                        <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: P.grey900, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                            Upload AWB list
                        </Typography>
                        <Typography sx={{ fontSize: "0.76rem", color: P.grey600, mt: 0.5, mb: 2 }}>
                            CSV or TXT · one AWB per line · header row auto-detected.
                        </Typography>

                        <FormProvider methods={methods} onSubmit={handleSubmit(onRequestSubmit)}>
                            <Box
                                sx={{
                                    border: `2px dashed ${selectedFileName ? P.primaryBorder : P.grey300}`,
                                    borderRadius: 2,
                                    backgroundColor: selectedFileName ? P.primaryLight : P.grey50,
                                    transition: "all 0.15s ease",
                                    p: 2,
                                    "&:hover": {
                                        borderColor: P.primary,
                                        backgroundColor: P.primaryLight,
                                    },
                                }}
                            >
                                <RHFFileField
                                    required
                                    fileType={["csv", "txt"]}
                                    name="awbFile"
                                    label={
                                        selectedFileName
                                            ? `✓ ${selectedFileName}`
                                            : "Click to choose a CSV / TXT file"
                                    }
                                />
                            </Box>

                            <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mt: 2, flexWrap: "wrap", rowGap: 1 }}>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="warning"
                                    disabled={isUploading}
                                    startIcon={
                                        isUploading ? (
                                            <CircularProgress size={14} sx={{ color: P.white }} />
                                        ) : (
                                            <FileUploadOutlinedIcon sx={{ fontSize: 16 }} />
                                        )
                                    }
                                    sx={{
                                        textTransform: "none",
                                        fontWeight: 600,
                                        fontSize: "0.82rem",
                                        borderRadius: 2,
                                        px: 2.5,
                                        py: 0.85,
                                        boxShadow: "none",
                                        "&:hover": { boxShadow: "none" },
                                    }}
                                >
                                    {isUploading ? "Submitting…" : "Submit Reset Job"}
                                </Button>
                                <Button
                                    variant="text"
                                    size="small"
                                    onClick={downloadSample}
                                    startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 15 }} />}
                                    sx={{
                                        textTransform: "none",
                                        fontWeight: 600,
                                        fontSize: "0.78rem",
                                        color: P.primary,
                                    }}
                                >
                                    Download sample
                                </Button>
                            </Stack>

                            {lastJobUuid && (
                                <Box
                                    sx={{
                                        mt: 2,
                                        p: 1.25,
                                        borderRadius: 2,
                                        border: `1px solid #a5d6a7`,
                                        backgroundColor: P.successLight,
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                    }}
                                >
                                    <CheckCircleOutlineIcon sx={{ fontSize: 16, color: P.success }} />
                                    <Typography sx={{ fontSize: "0.76rem", color: P.success, fontWeight: 500 }}>
                                        Job queued.{" "}
                                        <Box component="span" sx={{ fontFamily: "monospace", fontWeight: 700 }}>
                                            {lastJobUuid}
                                        </Box>
                                    </Typography>
                                </Box>
                            )}
                        </FormProvider>
                    </Grid>

                    {/* Side panel: what gets cleared */}
                    <Grid item xs={12} md={5}>
                        <Box
                            sx={{
                                backgroundColor: P.warningLight,
                                border: `1px solid ${P.warningBorder}`,
                                borderRadius: 2,
                                p: 2,
                                height: "100%",
                            }}
                        >
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}>
                                <WarningAmberIcon sx={{ color: P.warning, fontSize: 18 }} />
                                <Typography sx={{ fontSize: "0.76rem", fontWeight: 700, color: P.warning, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                                    What gets cleared
                                </Typography>
                            </Stack>
                            <Typography sx={{ fontSize: "0.74rem", color: P.grey800, mb: 1.25, lineHeight: 1.5 }}>
                                For every AWB, the following fields are set to <code>null</code>:
                            </Typography>
                            <Stack spacing={0.5}>
                                {RESET_FIELDS.map((f) => (
                                    <Box
                                        key={f}
                                        sx={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: 0.75,
                                            fontFamily: "monospace",
                                            fontSize: "0.72rem",
                                            color: P.grey900,
                                        }}
                                    >
                                        <Box sx={{ width: 4, height: 4, borderRadius: "50%", backgroundColor: P.warning }} />
                                        {f}
                                    </Box>
                                ))}
                            </Stack>
                            <Typography sx={{ fontSize: "0.68rem", color: P.grey600, mt: 1.5, lineHeight: 1.5 }}>
                                Previous values are captured in the result CSV for audit. This action is not auto-reversible.
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
            </Box>

            {/* ── Recent jobs ── */}
            <Box>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.25 }}>
                    <Stack direction="row" spacing={1.25} alignItems="baseline">
                        <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: P.grey900, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                            Recent Jobs
                        </Typography>
                        <Typography sx={{ fontSize: "0.7rem", color: P.grey600 }}>
                            Last 7 days
                        </Typography>
                    </Stack>
                    <Tooltip title="Refresh">
                        <IconButton onClick={() => setReload((r) => r + 1)} size="small" sx={{ color: P.grey600 }}>
                            <RefreshIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Tooltip>
                </Box>
                <Box
                    sx={{
                        backgroundColor: P.white,
                        border: `1px solid ${P.grey200}`,
                        borderRadius: 3,
                        overflow: "hidden",
                    }}
                >
                    {isLoading ? (
                        <Box sx={{ py: 5, textAlign: "center" }}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : !jobs || jobs.length === 0 ? (
                        <Box sx={{ py: 6, textAlign: "center", color: P.grey600 }}>
                            <AssignmentOutlinedIcon sx={{ fontSize: 36, opacity: 0.25 }} />
                            <Typography sx={{ fontSize: "0.85rem", mt: 1, fontWeight: 500 }}>
                                No reset jobs yet
                            </Typography>
                            <Typography sx={{ fontSize: "0.72rem", mt: 0.5 }}>
                                Submit a file above to see job activity here.
                            </Typography>
                        </Box>
                    ) : (
                        <Box sx={{ p: 1.5 }}>
                            <ProzoDataGrid
                                columns={columns}
                                rows={jobs || []}
                                sizePerPage={sizePerPage}
                                setSizePerPage={setSizePerPage}
                                page={page}
                                setPage={setPage}
                                pagination
                                paginationMode="client"
                                hideFooterPagination={false}
                                rowCount={jobs?.length || 0}
                                getRowId={(row) => row.id || row.uuid}
                            />
                        </Box>
                    )}
                </Box>
            </Box>

            {/* ── Confirmation modal (matches panel's MUIModal pattern) ── */}
            <MUIModal
                open={confirmOpen}
                handleClose={() => setConfirmOpen(false)}
                title="Confirm Reset"
                maxWidth="sm"
                hideClose
            >
                <Grid container>
                    <Grid item xs={12}>
                        <Typography variant="subtitle1">
                            <Typography variant="body1" sx={{ display: "inline" }}>
                                Do you want to queue a reset job for{" "}
                            </Typography>
                            <b>{pendingSubmitData?.awbFile?.[0]?.name}</b> ?
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1, color: P.grey600 }}>
                            For every AWB, the 6 lifecycle fields will be cleared. Previous values are
                            captured in the result CSV.
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sx={{ display: "flex", justifyContent: "end", pt: 2 }}>
                        <Button onClick={() => setConfirmOpen(false)} variant="contained" color="success">
                            NO
                        </Button>
                        <Button
                            onClick={runSubmit}
                            variant="contained"
                            color="error"
                            sx={{ ml: 1 }}
                        >
                            YES
                        </Button>
                    </Grid>
                </Grid>
            </MUIModal>

            {/* ── Details modal ── */}
            <MUIModal
                open={detailsOpen}
                title={detailsTitle}
                maxWidth="lg"
                handleClose={() => setDetailsOpen(false)}
            >
                <ProzoDataGrid
                    columns={detailsColumns}
                    rows={detailsRows}
                    sizePerPage={modalSizePerPage}
                    setSizePerPage={setModalSizePerPage}
                    page={modalPage}
                    setPage={setModalPage}
                    pagination
                    paginationMode="client"
                    hideFooterPagination={false}
                    rowCount={detailsRows?.length || 0}
                    getRowId={() => Math.random()}
                />
            </MUIModal>
        </Box>
    );
};

export default ResetTerminalStatus;
